import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, Package, Filter, Download, Loader2,
} from 'lucide-react';
import { vendorApi } from '@/services/api';
import { getVendorIdFromToken } from '@/services/auth';
import { unwrapList, toNumber } from '@nepal-ecom/shared-utils';
import { formatNPR } from '@/utils/format';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  Badge, Button, Card, CardContent, Input, cn, useConfirm,
} from '@nepal-ecom/ui';

type ProductStatus = 'active' | 'draft' | 'archived' | 'out_of_stock';

interface ApiProduct {
  id: string;
  name: string;
  sku?: string | null;
  slug: string;
  price: string | number;
  compareAtPrice?: string | number | null;
  stock: number;
  status: ProductStatus;
  totalSold: number;
  thumbnailUrl?: string | null;
  images?: string[];
  createdAt: string;
  updatedAt?: string;
  variants?: unknown[];
}

const STATUS_META: Record<ProductStatus, { label: string; variant: 'success' | 'secondary' | 'warning' | 'destructive' }> = {
  active:       { label: 'Active',       variant: 'success' },
  draft:        { label: 'Draft',        variant: 'warning' },
  archived:     { label: 'Archived',     variant: 'secondary' },
  out_of_stock: { label: 'Out of stock', variant: 'destructive' },
};

function StockChip({ stock }: { stock: number }) {
  let tone = 'text-emerald-700 bg-emerald-50';
  if (stock === 0) tone = 'text-red-700 bg-red-50';
  else if (stock < 10) tone = 'text-amber-700 bg-amber-50';
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold tabular-nums', tone)}>
      {stock} in stock
    </span>
  );
}

export function ProductList() {
  const confirm = useConfirm();
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProductStatus>('all');

  const load = async () => {
    const vendorId = getVendorIdFromToken();
    if (!vendorId) {
      setError('Not logged in as a vendor');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await vendorApi.getProducts({ vendorId, limit: 200, status: 'all' });
      setProducts(unwrapList<ApiProduct>(res.data?.data));
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        ?? (e as Error).message;
      setError(msg ?? 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [products, search, statusFilter]);

  const totals = useMemo(() => {
    const active = products.filter((p) => p.status === 'active').length;
    const draft = products.filter((p) => p.status === 'draft').length;
    return { active, draft };
  }, [products]);

  const handleDelete = async (p: ApiProduct) => {
    if (!(await confirm({
      title: `Delete "${p.name}"?`,
      description: 'This permanently removes the product and its variants. Existing order history is kept. This cannot be undone.',
      confirmLabel: 'Delete permanently',
      tone: 'danger',
    }))) return;
    try {
      await vendorApi.deleteProduct(p.id);
      toast.success('Product deleted', { description: p.name });
      await load();
    } catch (e) {
      toast.error('Delete failed', { description: (e as Error).message });
    }
  };

  const handleToggleVisibility = async (p: ApiProduct) => {
    const next: ProductStatus = p.status === 'active' ? 'archived' : 'active';
    try {
      await vendorApi.updateProduct(p.id, { status: next });
      toast.success(next === 'active' ? 'Product published' : 'Product hidden', { description: p.name });
      await load();
    } catch (e) {
      toast.error('Update failed', { description: (e as Error).message });
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Products"
        description={loading ? 'Loading…' : `${products.length} products in your store · ${totals.active} active · ${totals.draft} drafts`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>
              <Download className="h-4 w-4" /> Export
            </Button>
            <Button asChild>
              <Link to="/products/new">
                <Plus className="h-4 w-4" /> Add product
              </Link>
            </Button>
          </div>
        }
      />

      {error && (
        <Card className="mb-5">
          <CardContent className="p-4 text-sm text-red-700 bg-red-50 rounded-lg">{error}</CardContent>
        </Card>
      )}

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products by name or SKU…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            >
              <option value="all">All status</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Product', 'Status', 'Price', 'Stock', 'Sold', 'Updated', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState
                        icon={Package}
                        title={products.length === 0 ? 'No products yet' : 'No products match your filters'}
                        description={products.length === 0
                          ? 'Add your first product to start selling on Shishyastra.'
                          : 'Try clearing search or adjusting the status filter.'}
                        action={
                          <Button asChild>
                            <Link to="/products/new"><Plus className="h-4 w-4" /> Add product</Link>
                          </Button>
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  filtered.map((p) => {
                    const meta = STATUS_META[p.status] ?? STATUS_META.draft;
                    const price = toNumber(p.price);
                    const compareAt = toNumber(p.compareAtPrice);
                    return (
                      <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                              {p.thumbnailUrl
                                ? <img src={p.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                                : <Package className="h-5 w-5 m-3.5 text-slate-400" />}
                            </div>
                            <div className="min-w-0">
                              <Link to={`/products/${p.id}`} className="text-sm font-semibold text-slate-900 hover:text-primary-700 truncate block max-w-xs">
                                {p.name}
                              </Link>
                              <p className="text-xs text-slate-500 truncate">
                                {p.sku ?? `SKU-${p.id.slice(-4).toUpperCase()}`}
                                {p.variants && p.variants.length > 0 && (
                                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-cobalt-700 bg-cobalt-50 px-1.5 py-0.5 rounded">
                                    {p.variants.length} variants
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-slate-900 tabular-nums">{formatNPR(price)}</p>
                          {compareAt > price && (
                            <p className="text-xs text-slate-400 line-through tabular-nums">{formatNPR(compareAt)}</p>
                          )}
                        </td>
                        <td className="px-4 py-3"><StockChip stock={p.stock} /></td>
                        <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{p.totalSold}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                          {new Date(p.updatedAt ?? p.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-0.5">
                            <Link
                              to={`/products/${p.id}`}
                              className="p-1.5 rounded-md text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleToggleVisibility(p)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-cobalt-600 hover:bg-cobalt-50 transition-colors"
                              title={p.status === 'active' ? 'Hide' : 'Show'}
                            >
                              {p.status === 'active' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(p)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
